/// <amd-module name="Dashboards/AddToDashboard/AddToDashboardPanel" />
import * as Resources from "Dashboards/AddToDashboard/Resources";
import { Dashboard, DashboardScope } from "Dashboards/Common/Contracts/Dashboard";
import { DashboardOwner, getMyTeams } from "Dashboards/DashboardPickerProvider/DashboardPickerProviderFactory";
import * as React from "react";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureEnabled } from "VSS/Platform/Feature";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { IButtonProps } from "VSSUI/Button";
import { Panel } from "VSSUI/Panel";
import { IPickListGroup } from "VSSUI/PickList";
import { Spinner } from "VSSUI/Spinner";
import { DashboardPickerItem, SimpleDashboardPicker } from "./SimpleDashboardPicker";

export interface AddToDashboardPanelProps {
    isOpen: boolean;

    /** The name of the Widget to copy to a dashboard */
    widgetName: string;

    onDismiss: () => void;

    /**
     * Returns information about the selected dashboard.
     */
    onOk: (dashboard: Dashboard) => void;
}

export interface AddToDashboardPanelState {
    selectedItem: DashboardPickerItem | undefined;

    dashboards: DashboardPickerItem[] | undefined;
    groups: IPickListGroup[] | undefined;
}

/** This panel is used to allow a user to select a Dashboard from the current project.
 *  Note for testing: The search box only appears if a minimum required number of elements appears.
 */
export class AddToDashboardPanel extends VssComponent<AddToDashboardPanelProps, AddToDashboardPanelState> {
    private static readonly allGroup: IPickListGroup = { key: "add-to-dashboard-all-group", name: Resources.AllGroupHeader };
    private static readonly allTeamDashboardsGroup: IPickListGroup = {
        key: "add-to-dashboard-all-team-dashboards-group",
        name: Resources.AllTeamDashboardsGroupHeader
    };
    private static readonly allProjectDashboardsGroup: IPickListGroup = {
        key: "add-to-dashboard-all-project-dashboards-group",
        name: Resources.AllProjectDashboardsGroupHeader
    };
    private static readonly favoritesGroup: IPickListGroup = { key: "add-to-dashboard-favorite-group", name: Resources.FavoritesGroupHeader };

    constructor(props: AddToDashboardPanelProps, context: IVssComponentContext) {
        super(props, context);

        this.state = {
            selectedItem: undefined,
            dashboards: undefined,
            groups: undefined
        };
    }
    public async componentDidMount(): Promise<void> {
        this.publish("Dashboards.AddToDashboardPanel.Loaded");

        const data = await this.fetchData();
        this.processData(data.allDashboards, data.favoriteDashboards, data.allOwners, data.myTeams);
    }

    public render(): JSX.Element | null {
        if (this.props.isOpen) {
            const footerButtons: IButtonProps[] = [
                {
                    className: "widget-config-save",
                    onClick: this.onOk,
                    text: Resources.PanelOk,
                    primary: true,
                    disabled: this.state.selectedItem === undefined
                },
                {
                    className: "widget-config-cancel",
                    onClick: this.onDismiss,
                    text: Resources.PanelCancel
                }
            ];
            const isReady = this.state.dashboards !== undefined && this.state.groups !== undefined;

            return (
                <Panel
                    className="add-to-dashboard-panel"
                    onDismiss={this.props.onDismiss}
                    titleProps={{ text: Resources.PanelTitle }}
                    footerButtonProps={footerButtons}
                >
                    <div>
                        <FormatComponent format={Resources.PanelInstruction}>
                            <b>{this.props.widgetName}</b>
                        </FormatComponent>
                        {!isReady && <Spinner />}

                        {isReady && (
                            <SimpleDashboardPicker
                                dashboards={this.state.dashboards!}
                                onSelection={this.onSelectedDashboardChanged}
                                selectedItem={this.state.selectedItem}
                                groups={this.state.groups!}
                            />
                        )}
                    </div>
                </Panel>
            );
        }
        return null;
    }

    private onSelectedDashboardChanged = (dashboard: DashboardPickerItem): void => {
        this.setState({ selectedItem: dashboard });
    };

    private onOk = () => {
        this.props.onOk(this.state.selectedItem!);
        this.publish("Dashboards.AddToDashboardPanel.Ok");
    };

    private onDismiss = () => {
        this.props.onDismiss();
        this.publish("Dashboards.AddToDashboardPanel.Dismiss");
    };

    private publish(featureName: string) {
        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent("Dashboards", featureName, {});
    }

    private async fetchData(): Promise<{
        allDashboards: Dashboard[];
        favoriteDashboards: Dashboard[];
        allOwners: DashboardOwner[];
        myTeams: DashboardOwner[];
    }> {
        const contributionService: IVssContributionService = this.context.pageContext.getService("IVssContributionService");

        const allDashboardsPromise = contributionService.getDataAsync<{ dashboards: Dashboard[]; owners: DashboardOwner[] }>(
            "ms.vss-dashboards-web.all-dashboards-data-provider"
        );
        const favoriteDashboardsPromise = contributionService.getDataAsync<{ dashboard: Dashboard; owner: DashboardOwner }[]>(
            "ms.vss-dashboards-web.favorite-dashboards-data-provider"
        );
        const myTeamsPromise = getMyTeams(this.context.pageContext);

        const allDashboardsResult = await allDashboardsPromise;
        const allDashboards = (allDashboardsResult && allDashboardsResult.dashboards) || [];
        const allOwners = (allDashboardsResult && allDashboardsResult.owners) || [];

        const favoriteDashboardsResult = await favoriteDashboardsPromise;
        const favoriteDashboards = (favoriteDashboardsResult && favoriteDashboardsResult.map(item => item.dashboard)) || [];

        const myTeamsResult = await myTeamsPromise;
        const myTeams = myTeamsResult || [];

        return { allDashboards, favoriteDashboards, allOwners: allOwners, myTeams };
    }

    private processData(allDashboards: Dashboard[], favoriteDashboards: Dashboard[], allOwners: DashboardOwner[], myTeams: DashboardOwner[]): void {
        const isProjectDashboardsEnabled = isFeatureEnabled(this.context.pageContext, "ms.vss-dashboards.team-agnostic-dashboards-feature", false);
        const allOwnersNameMap: { [key: string]: string } = {};
        for (const owner of allOwners) {
            allOwnersNameMap[owner.id] = owner.name;
        }

        const dashboardPickerItems: DashboardPickerItem[] = [];

        // Copy all team dashboards with their team as a group key to show them in the team groups.
        // Dashboards with teams not matching any of the teams in `myTeams` aren't shown.
        const teamDashboards = allDashboards.filter(d => allOwnersNameMap[d.ownerId] && d.dashboardScope === DashboardScope.Project_Team);
        dashboardPickerItems.push(
            ...teamDashboards.map(d => {
                return { ...d, groupKey: d.ownerId, displayName: d.name };
            })
        );

        // Copy all project and team dashboards under their respective categories.
        if (isProjectDashboardsEnabled) {
            dashboardPickerItems.push(
                ...allDashboards.map(d => {
                    const ownerName = allOwnersNameMap[d.ownerId];
                    const groupKey =
                        d.dashboardScope === DashboardScope.Project
                            ? AddToDashboardPanel.allProjectDashboardsGroup.key
                            : AddToDashboardPanel.allTeamDashboardsGroup.key;
                    const displayName: string = ownerName ? `${ownerName} - ${d.name}` : `${d.name}`;

                    return {
                        ...d,
                        groupKey,
                        displayName
                    };
                })
            );
        } else {
            // Copy all team dashboards with the "all" group key.
            dashboardPickerItems.push(
                ...teamDashboards.map(d => {
                    const teamName = allOwnersNameMap[d.ownerId];
                    return {
                        ...d,
                        groupKey: AddToDashboardPanel.allGroup.key,
                        displayName: `${teamName} - ${d.name}`
                    };
                })
            );
        }

        // Copy favorite dashboards with the "favorite" group key.
        dashboardPickerItems.push(
            ...favoriteDashboards.map(d => {
                const ownerName = allOwnersNameMap[d.ownerId];
                const displayName: string = ownerName ? `${ownerName} - ${d.name}` : `${d.name}`;
                return {
                    ...d,
                    groupKey: AddToDashboardPanel.favoritesGroup.key,
                    displayName
                };
            })
        );

        // Sort the items to show them in each group in alphabetical order.
        dashboardPickerItems.sort((a, b) => localeIgnoreCaseComparer(a.displayName, b.displayName));

        // Create groups for the teams, sort them to display in alphabetical order,
        // and sandwich them between the favorites and all groups.
        const groups: IPickListGroup[] = [
            AddToDashboardPanel.favoritesGroup,
            ...myTeams
                .map(item => {
                    return { key: item.id, name: item.name };
                })
                .sort((a, b) => localeIgnoreCaseComparer(a.name, b.name))
        ];

        if (isProjectDashboardsEnabled) {
            groups.push(AddToDashboardPanel.allTeamDashboardsGroup);
            groups.push(AddToDashboardPanel.allProjectDashboardsGroup);
        } else {
            groups.push(AddToDashboardPanel.allGroup);
        }

        this.setState({ dashboards: dashboardPickerItems, groups });
    }
}
